<template>
  <CarvisInput
    input="div"
    :label="label"
  >
    <Combobox
      v-slot="{ open }"
      as="div"
      class="relative"
      :class="{ 'opacity-30 cursor-not-allowed': disabled }"
      :disabled="disabled"
      :model-value="modelValue"
      @update:model-value="onSelect"
    >
      <ComboboxButton
        as="div"
        class="flex justify-between w-full items-center"
      >
        <ComboboxInput
          v-bind="$attrs"
          :aria-label="label"
          class="nested-input-field"
          :loading="loading"
          :value="inputDisplayValue"
          @blur="$emit('blur')"
          @change="onInputChange"
        />
        <ToggleDropdownIcon :open="open" />
      </ComboboxButton>
      <FlyoutMenuTransition>
        <ComboboxOptions
          v-if="filteredItems.length > 0 || items === null"
          class="absolute flyout-menu-items mt-1.5 max-h-48 overflow-y-auto"
          :class="{ 'cursor-not-allowed': items === null }"
        >
          <div
            v-if="items === null"
            class="w-full flex justify-center p-2"
          >
            <CarvisSpinner class="w-5" />
          </div>
          <ComboboxOption
            v-for="item in filteredItems"
            v-else
            :key="item.value"
            v-slot="{ active }"
            :value="item"
          >
            <div
              class="flyout-menu-item flex-col items-start!"
              :class="{ 'bg-gray-200': active }"
            >
              <div :class="{ 'font-bold text-blue-dark': modelValue === item.value }">
                {{ item.label ?? item.value }}
              </div>
              <span
                v-if="item.description"
                class="text-xs font-light m-0!"
              >
                {{ item.description }}
              </span>
            </div>
          </ComboboxOption>
        </ComboboxOptions>
      </FlyoutMenuTransition>
    </Combobox>
  </CarvisInput>
</template>

<script setup lang="ts">
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/vue'
import { computed, ref } from 'vue'

import ToggleDropdownIcon from '@/components/dropdown/ToggleDropdownIcon.vue'
import FlyoutMenuTransition from '@/components/flyout/FlyoutMenuTransition.vue'

type Item = { value: string, label?: string, description?: string }

const props = defineProps<{
  items: Item[] | null
  modelValue?: string | null
  disabled?: boolean
  loading?: boolean
  label: string
}>()

const query = ref('')

const selectedItem = computed(() =>
  props.items?.find(i => i.value === props.modelValue || i.label === props.modelValue))

const inputDisplayValue = computed(() => {
  if (query.value) return query.value
  if (!props.modelValue) return ''
  return selectedItem.value?.label ?? props.modelValue
})

const allItems = computed(() => {
  const items = [...(props.items ?? [])]
  if (props.modelValue && !selectedItem.value) {
    items.push({ value: props.modelValue })
  }
  return items.sort((a, b) => {
    if (a.value === props.modelValue) return -1
    if (b.value === props.modelValue) return 1
    return (a.label ?? a.value).localeCompare(b.label ?? b.value)
  })
})

const filteredItems = computed(() => {
  if (!query.value) return allItems.value
  const q = query.value.trim().toLowerCase()
  return allItems.value.filter(item => {
    const text = item.label ?? item.value
    return text.toLowerCase().includes(q)
  })
})

const onInputChange = (event: Event): void => {
  const target = event.target as HTMLInputElement
  query.value = target.value
  emit('update:modelValue', target.value)
}

const onSelect = (item: Item): void => {
  query.value = ''
  emit('update:modelValue', item.value)
}

const emit = defineEmits(['update:modelValue', 'blur'])
</script>
